// src/app/meter/components/meter-analytics/meter-heatmap/meter-heatmap.component.ts

import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import 'leaflet.heat';
import { MeterGeoPoint } from '../../../models/meter-analytics.model';

@Component({
    selector: 'app-meter-heatmap',
    templateUrl: './meter-heatmap.component.html',
    styleUrls: ['./meter-heatmap.component.scss'],
})
export class MeterHeatmapComponent implements OnChanges, OnDestroy {
    @Input() points: MeterGeoPoint[] = [];

    private map: L.Map;
    private heatLayer: any;
    private mapReady = false;
    private defaultCenter: L.LatLngExpression = [45.3182, -75.098];
    private defaultZoom = 13;

    get activePoints(): MeterGeoPoint[] {
        return this.points.filter((p) => p.isActive);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.points && this.points?.length > 0 && !this.mapReady) {
            setTimeout(() => this.initMap(), 100);
        } else if (this.mapReady && changes.points) {
            this.updateHeatmap();
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    private initMap(): void {
        const container = document.getElementById('meter-analytics-heatmap');
        if (!container || container.clientHeight === 0) {
            setTimeout(() => this.initMap(), 200);
            return;
        }

        if (this.map) {
            this.map.remove();
        }

        this.map = L.map('meter-analytics-heatmap', {
            zoomControl: false,
        }).setView(this.defaultCenter, this.defaultZoom);

        L.control.zoom({ position: 'topright' }).addTo(this.map);

        L.tileLayer(MAP_TILE_URL, {
            maxZoom: 18,
            minZoom: 3,
            attribution: MAP_TILE_ATTRIBUTION,
        }).addTo(this.map);

        this.mapReady = true;
        this.updateHeatmap();
    }

    private updateHeatmap(): void {
        if (!this.map) return;

        if (this.heatLayer) {
            this.map.removeLayer(this.heatLayer);
            this.heatLayer = null;
        }

        if (this.points.length === 0) return;

        const maxConsumption = Math.max(...this.points.map((p) => p.totalConsumption), 1);

        const heatData: L.HeatLatLngTuple[] = this.points.map((p) => [p.latitude, p.longitude, p.totalConsumption / maxConsumption]);

        this.heatLayer = (L as any)
            .heatLayer(heatData, {
                radius: 25,
                blur: 15,
                maxZoom: 17,
                max: 1,
                gradient: { 0.4: '#06b6d4', 0.65: '#f59e0b', 1: '#ef4444' },
            })
            .addTo(this.map);

        const bounds = L.latLngBounds(this.points.map((p) => [p.latitude, p.longitude] as L.LatLngTuple));
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
}
